import { getCachedEventAdminPanelAccess } from '../../features/events/event-access';

export type AppRole = 'user' | 'b2b_user' | 'b2b_admin';

export interface RouteAccessMeta {
    path: string;
    roles: AppRole[];
    featureFlag?: string;
    menu?: {
        key: string;
        icon: string;
        order: number;
    };
}

const normalizeRole = (value: unknown) => String(value ?? '').trim().toLowerCase();

const readUserDetails = () => {
    try {
        const raw = window.localStorage.getItem('userDetails');
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const readActiveEvent = () => {
    try {
        const raw = window.localStorage.getItem('activeEvent');
        const parsed = raw ? JSON.parse(raw) : null;
        // Ortak secim baska bir etkinlige gecmis ama anlik goruntu eskiyse (henuz
        // yenilenmedi) eski etkinligin yetkileri kullanilmaz.
        const selectedId = window.localStorage.getItem('selectedActiveEventId') || '';
        const snapshotId = String(parsed?.activeEventId || parsed?.eventId || parsed?.event?.id || '').trim();
        if (selectedId && snapshotId && selectedId !== snapshotId) return null;
        return parsed;
    } catch {
        return null;
    }
};

const resolveEventVisibility = () => {
    const activeEvent = readActiveEvent();
    const directParticipants = activeEvent?.canSeeParticipants;
    const directVisitors = activeEvent?.canSeeVisitors;
    const nestedParticipants = activeEvent?.event?.canSeeParticipants;
    const nestedVisitors = activeEvent?.event?.canSeeVisitors;
    const hasSnapshot =
        typeof directParticipants === 'boolean' ||
        typeof directVisitors === 'boolean' ||
        typeof nestedParticipants === 'boolean' ||
        typeof nestedVisitors === 'boolean';

    return {
        canSeeParticipants: Boolean(directParticipants ?? nestedParticipants),
        canSeeVisitors: Boolean(directVisitors ?? nestedVisitors),
        hasSnapshot,
    };
};

const resolveActiveEventViewerRoles = (): string[] => {
    const activeEvent = readActiveEvent();
    const directViewerRoles = Array.isArray(activeEvent?.viewerRoles) ? activeEvent.viewerRoles : [];
    const nestedViewerRoles = Array.isArray(activeEvent?.event?.viewerRoles) ? activeEvent.event.viewerRoles : [];
    return [...directViewerRoles, ...nestedViewerRoles]
        .map(normalizeRole)
        .filter(Boolean);
};

const isOrganizerUser = (userDetails: any): boolean => {
    if (userDetails?.isAdmin) return true;
    if (userDetails?.isOrganizer) return true;
    const loginRole = String(userDetails?.loginRole || '').toLowerCase();
    if (loginRole === 'admin' || loginRole === 'organizer') return true;
    return false;
};

const canAccessAllMenusAndActions = (): boolean => {
    const userDetails = readUserDetails();
    if (!userDetails) return false;
    if (isOrganizerUser(userDetails)) return true;
    return Boolean(getCachedEventAdminPanelAccess()?.canAccessAdminPanel);
};

const isEventAdminUser = (userDetails: any): boolean => {
    if (!userDetails) return false;
    const appRole = String(userDetails?.appRole || '').trim().toLowerCase();
    if (appRole === 'b2b_admin') return true;
    const loginRole = String(userDetails?.loginRole || userDetails?.role || userDetails?.user?.role || '')
        .trim()
        .toLowerCase();
    if (loginRole === 'manager' || loginRole === 'yonetici' || loginRole === 'yönetici') return true;
    return Boolean(getCachedEventAdminPanelAccess()?.canAccessAdminPanel);
};

const isB2BAdminOnlyRoute = (route: RouteAccessMeta): boolean => {
    const roles = Array.isArray(route.roles) ? route.roles : [];
    return roles.length > 0 && roles.every((role) => role === 'b2b_admin');
};

// DIKKAT: 'home' bilerek bu listede DEGIL. Giristen sonra rolu ne olursa olsun
// herkes `/home`'a yonlendiriliyor (bkz. resolveUserDashboardPath). Burada
// filtrelenirse organizator/admin hesaplarda `/home` rotasi hic olusmuyor ve
// kullanici gormesi gereken sayfa yerine 404 goruyordu.
const B2B_USER_ONLY_ROUTE_PATHS = new Set([
    'sessions',
    'announcements',
    'b2b-announcements',
    'b2b/announcements',
    'visitors',
    'users',
    'users/:userId',
    'appointments',
    'my-appointments',
    'meetings/list',
    'meetings/:meetingId',
    'tables',
    'tables/:tableId',
    'masa-randevularim',
]);

const EVENT_VISITOR_ROUTE_PATHS = new Set([
    'visitors',
]);

const INFORMATION_FORM_ROUTE_PATHS = new Set([
    'stand-participants-information-form',
    'stand-participants-information-form-list',
]);

type EventVisibilitySnapshot = {
    canSeeParticipants: boolean;
    canSeeVisitors: boolean;
    hasSnapshot: boolean;
    viewerRoles?: string[];
};

export const resolveAppRole = (): AppRole => {
    const userDetails = readUserDetails();
    const loginRole = String(userDetails?.loginRole || userDetails?.role || userDetails?.user?.role || '')
        .trim()
        .toLowerCase();
    const storedAppRole = String(userDetails?.appRole || '').trim().toLowerCase();
    if (storedAppRole === 'b2b_admin') return 'b2b_admin';
    if (loginRole === 'admin' || loginRole === 'manager' || loginRole === 'yonetici' || loginRole === 'yönetici') {
        return 'b2b_admin';
    }
    if (canAccessAllMenusAndActions()) return 'b2b_admin';
    return 'user';
};

export const canAccessInformationFormFromVisibility = (
    eventVisibility: EventVisibilitySnapshot | null | undefined,
): boolean => {
    const viewerRoles = Array.isArray(eventVisibility?.viewerRoles)
        ? eventVisibility.viewerRoles.map(normalizeRole).filter(Boolean)
        : [];
    if (viewerRoles.includes('participant')) {
        return true;
    }
    if (viewerRoles.includes('visitor') || viewerRoles.includes('buyermission')) {
        return false;
    }

    if (!eventVisibility?.hasSnapshot) {
        return false;
    }

    return Boolean(eventVisibility.canSeeParticipants);
};

export const canAccessInformationForm = (): boolean => {
    const eventVisibility = resolveEventVisibility();
    const viewerRoles = resolveActiveEventViewerRoles();
    return canAccessInformationFormFromVisibility({
        ...eventVisibility,
        viewerRoles,
    });
};

export const getRoutesForRole = <T extends RouteAccessMeta>(role: AppRole, routes: T[]): T[] => {
    const userDetails = readUserDetails();
    const eventAdminWithCombinedAccess = isEventAdminUser(userDetails);
    if (eventAdminWithCombinedAccess) {
        return routes;
    }

    const canSeeVisitors = true;
    const canSeeInformationForm = canAccessInformationForm();

    const hasAdminPanelAccess = canAccessAllMenusAndActions();

    if (role === 'b2b_admin' || hasAdminPanelAccess) {
        return routes.filter((route) => {
            if (EVENT_VISITOR_ROUTE_PATHS.has(route.path)) return canSeeVisitors;
            if (isB2BAdminOnlyRoute(route)) return hasAdminPanelAccess;
            return !B2B_USER_ONLY_ROUTE_PATHS.has(route.path);
        });
    }
    return routes.filter((route) => {
        if (EVENT_VISITOR_ROUTE_PATHS.has(route.path)) return canSeeVisitors;
        if (INFORMATION_FORM_ROUTE_PATHS.has(route.path)) return canSeeInformationForm;
        return !isB2BAdminOnlyRoute(route);
    });
};

export const getMenuRoutesForRole = <T extends RouteAccessMeta>(role: AppRole, routes: T[]): T[] => {
    return getRoutesForRole(role, routes)
        .filter((route) => route.menu)
        .sort((a, b) => (a.menu?.order || 0) - (b.menu?.order || 0));
};
